import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import '../styles/ui.css';

/*
 * "Instalar o GyMag no telemóvel": o GyMag é uma aplicação web (PWA) — instala-se pelo browser, sem loja nem ficheiros.
 *  - Android / Chrome, Edge: o browser oferece a instalação (beforeinstallprompt) e o botão dispara-a.
 *  - iPhone / iPad (Safari): não há API; mostra-se o caminho "Partilhar → Adicionar ao ecrã principal".
 * Não aparece se já está instalada (modo standalone), se o browser não consegue instalar, ou depois de a dispensar (volta passados 30 dias).
 */

let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferredPrompt = e; notify(); });
  window.addEventListener('appinstalled', () => { deferredPrompt = null; notify(); });
}

const KEY = 'gymag.install.dismissedAt';
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const dismissedRecently = () => {
  try { const t = Number(localStorage.getItem(KEY)); return Number.isFinite(t) && t > 0 && Date.now() - t < THIRTY_DAYS; } catch { return false; }
};

export const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  try { if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true; } catch { /* sem matchMedia */ }
  return window.navigator.standalone === true;
};

export const isIosSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
  const safari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);   // Chrome/Firefox no iPhone não instalam: só o Safari
  return ios && safari;
};

export default function InstallHint() {
  const [, force] = useState(0);
  const [hidden, setHidden] = useState(dismissedRecently);

  useEffect(() => {
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);

  if (hidden || isStandalone()) return null;
  const canPrompt = !!deferredPrompt;
  const ios = !canPrompt && isIosSafari();
  if (!canPrompt && !ios) return null;   // não se oferece o que este browser não consegue fazer

  const dismiss = () => { try { localStorage.setItem(KEY, String(Date.now())); } catch { /* sem armazenamento */ } setHidden(true); };
  const install = async () => {
    const prompt = deferredPrompt;
    if (!prompt) return;
    deferredPrompt = null;
    try { await prompt.prompt(); await prompt.userChoice; } catch { /* o utilizador fechou */ }
    notify();
  };

  return (
    <section className="gm-card gm-col-12 install-hint" aria-label="Instalar o GyMag" data-testid="install-hint">
      <div className="install-hint-row">
        <span className="install-hint-icon" aria-hidden="true">{ios ? <Share size={20} /> : <Download size={20} />}</span>
        <div className="install-hint-text">
          <strong>Instale o GyMag no telemóvel</strong>
          {ios
            ? <span className="gm-note">No Safari, toque em <b>Partilhar</b> e depois em <b>Adicionar ao ecrã principal</b>. Abre como uma aplicação, sem barras do browser.</span>
            : <span className="gm-note">Abre como uma aplicação, em ecrã inteiro, e o treino fica a um toque.</span>}
        </div>
        {canPrompt ? <button type="button" className="gm-btn gm-btn-primary gm-btn-sm" onClick={install} data-testid="install-btn">Instalar</button> : null}
        <button type="button" className="install-hint-close" onClick={dismiss} aria-label="Dispensar"><X size={18} aria-hidden="true" /></button>
      </div>
    </section>
  );
}
